import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { AppLink } from '../components/Nav';
import { Thumbnail } from '../components/VideosTable';
import { ExternalLink, Fact, Facts, Identity, Mono, Section, VideoBadge } from '../components/data';
import { Badge, ErrorState, Loading, PageHeader, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { bytes, dateTime, shortDate, timeAgo, timecode } from '../lib/format';
import { fetchVideo, qk, type VideoDetail, type VideoSettings } from '../queries/admin';

/**
 * One recording, for support: what it is, who made it, how it is set, what
 * the transcript says, who said what. The player is the plainest thing
 * that plays the CDN's HLS: the app's own page is one click away for
 * anything fancier.
 */

const VISIBILITY: Record<VideoSettings['visibility'], string> = {
  public: 'Anyone with the link',
  signedIn: 'Anyone signed in',
  team: 'The team',
  invited: 'Named people',
  private: 'Owner only',
};

const GATE: Record<VideoSettings['emailGate'], string> = { none: 'No', ask: 'Asked for', require: 'Required' };

const REACTION_EMOJI: Record<string, string> = { like: '👍', love: '❤️', laugh: '😂', wow: '😮', clap: '👏', think: '🤔' };

function Player({ video }: { video: VideoDetail }) {
  const ref = useRef<HTMLVideoElement & HTMLAudioElement>(null);
  const hlsUrl = video.playback?.hlsUrl ?? null;
  const mp4Url = video.playback?.mp4Url ?? video.rawPlaybackUrl;

  useEffect(() => {
    const media = ref.current;
    if (!media) return;
    if (!hlsUrl) {
      if (mp4Url) media.src = mp4Url;
      return;
    }
    // Safari plays HLS itself; everyone else gets hls.js, loaded only here.
    if (media.canPlayType('application/vnd.apple.mpegurl')) {
      media.src = hlsUrl;
      return;
    }
    let cancelled = false;
    let instance: { destroy: () => void } | null = null;
    import('hls.js').then(({ default: Hls }) => {
      if (cancelled) return;
      if (!Hls.isSupported()) {
        if (mp4Url) media.src = mp4Url;
        return;
      }
      const hls = new Hls();
      hls.loadSource(hlsUrl);
      hls.attachMedia(media);
      instance = hls;
    });
    return () => {
      cancelled = true;
      instance?.destroy();
    };
  }, [hlsUrl, mp4Url]);

  if (!hlsUrl && !mp4Url) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-md border border-hairline bg-slate-900 text-body text-chalk-faint">
        {video.status === 'failed' ? 'The transcode failed; there is nothing to play.' : 'Nothing to play yet.'}
      </div>
    );
  }
  if (video.kind === 'audio') {
    return (
      <div className="rounded-md border border-hairline bg-slate-900 p-4">
        <audio ref={ref} controls preload="metadata" className="w-full" />
      </div>
    );
  }
  return (
    <video
      ref={ref}
      controls
      playsInline
      preload="metadata"
      poster={video.posterUrl ?? undefined}
      className="aspect-video w-full rounded-md border border-hairline bg-black"
    >
      {video.playback?.captionsUrl ? <track kind="captions" src={video.playback.captionsUrl} srcLang={video.transcript?.language ?? 'en'} label="Captions" /> : null}
    </video>
  );
}

function Setting({ label, value, on }: { label: string; value?: string; on?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-hairline py-2 last:border-b-0">
      <span className="text-body text-chalk-dim">{label}</span>
      {value !== undefined ? (
        <span className="text-body text-chalk">{value}</span>
      ) : (
        <span className={cx('text-caption', on ? 'text-go' : 'text-chalk-faint')}>{on ? 'On' : 'Off'}</span>
      )}
    </div>
  );
}

export function RecordingPage({ id }: { id: string }) {
  const video = useQuery({ queryKey: qk.video(id), queryFn: () => fetchVideo(id) });

  useEffect(() => {
    if (video.data) track('Viewed Admin Recording', { video_id: video.data.shareId });
  }, [video.data]);

  if (video.isPending) {
    return (
      <>
        <PageHeader title="Recording" />
        <Loading />
      </>
    );
  }
  if (video.isError) {
    return (
      <>
        <PageHeader title="Recording" />
        <ErrorState message={errorMessage(video.error)} onRetry={() => void video.refetch()} />
      </>
    );
  }

  const data = video.data;
  const settings = data.settings;
  const totals = Object.entries(data.reactionTotals).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <p className="mb-3 text-caption text-chalk-faint">
        <AppLink to="/recordings" className="hover:text-chalk-dim">
          Recordings
        </AppLink>{' '}
        /
      </p>
      <PageHeader
        title={data.title}
        subtitle={data.summary ?? undefined}
        actions={
          <span className="flex items-center gap-2">
            <VideoBadge status={data.status} deleted={data.deletedAt !== null} />
            {data.kind === 'audio' ? <Badge tone="neutral">Audio</Badge> : null}
            <ExternalLink href={data.shareUrl}>Open in the app</ExternalLink>
          </span>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div>
          <Player video={data} />
          {data.tags.length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Tags">
              {data.tags.map((tag) => (
                <li key={tag.id} className={cx('rounded-full border bg-slate-900 px-2.5 py-0.5 text-caption text-chalk', tag.scope === 'personal' ? 'border-dashed border-chalk/30' : 'border-hairline')}>
                  {tag.name}
                  <span className="ml-1.5 text-chalk-faint">{tag.scope === 'team' ? 'team' : "owner's"}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <Facts>
          <Fact label="By">
            {data.owner ? (
              <AppLink to="/users/$id" params={{ id: data.owner.id }} className="hover:underline">
                <Identity name={data.owner.name} email={data.owner.email} avatarUrl={data.owner.avatarUrl} />
              </AppLink>
            ) : data.submitterName ? (
              `${data.submitterName} (request link)`
            ) : null}
          </Fact>
          <Fact label="Where">
            {data.org ? (
              <AppLink to="/teams/$id" params={{ id: data.org.id }} className="hover:underline">
                {data.org.name}
              </AppLink>
            ) : (
              'Personal'
            )}
          </Fact>
          <Fact label="Recorded">
            {shortDate(data.createdAt)} <span className="text-chalk-faint">({timeAgo(data.createdAt)})</span>
          </Fact>
          <Fact label="Length">{timecode(data.durationSec)}</Fact>
          <Fact label="Size">{data.sizeBytes ? bytes(data.sizeBytes) : null}</Fact>
          <Fact label="Frame">{data.width && data.height ? `${data.width} × ${data.height}` : null}</Fact>
          <Fact label="Views">{data._count.views.toLocaleString()}</Fact>
          <Fact label="Comments">{data._count.comments.toLocaleString()}</Fact>
          <Fact label="Reactions">{data._count.reactions.toLocaleString()}</Fact>
          <Fact label="Title from">{data.titleSource === 'ai' ? 'The transcript' : data.titleSource === 'user' ? 'The owner' : 'The date'}</Fact>
          <Fact label="Edits">{data.editVersion > 0 ? `${data.editVersion}${data.renderingVersion !== null ? ', one rendering' : ''}` : 'None'}</Fact>
          <Fact label="Share id">
            <Mono>{data.shareId}</Mono>
          </Fact>
          <Fact label="Video id">
            <Mono>{data.id}</Mono>
          </Fact>
          {data.deletedAt ? <Fact label="Deleted">{dateTime(data.deletedAt)}</Fact> : null}
        </Facts>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Settings">
          <div className="card px-4 py-2">
            <Setting label="Who can watch" value={VISIBILITY[settings.visibility]} />
            <Setting label="Email to view" value={GATE[settings.emailGate]} />
            <Setting label="Captions on by default" on={settings.captionsOn} />
            <Setting label="Recommended speed" value={`${settings.playbackRate}×`} />
            <Setting label="Comments" on={settings.commentsEnabled} />
            <Setting label="Emoji reactions" on={settings.reactionsEnabled} />
            <Setting label="Download" on={settings.downloadEnabled} />
            <Setting label="View count shown" on={settings.analyticsPublic} />
            <Setting label="Transcript shown" on={settings.transcriptPublic} />
            <Setting label="Links at the end" on={settings.linksAtEnd} />
          </div>
        </Section>

        <Section title="Reactions" count={data.reactions.length}>
          <div className="card p-4">
            {totals.length === 0 ? (
              <p className="text-body text-chalk-faint">None yet.</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {totals.map(([kind, count]) => (
                    <span key={kind} className="rounded-full border border-hairline bg-slate-900 px-2.5 py-0.5 text-body text-chalk">
                      {REACTION_EMOJI[kind] ?? kind} <span className="tnum text-chalk-dim">{count}</span>
                    </span>
                  ))}
                </div>
                <ul className="mt-3 max-h-56 space-y-1 overflow-auto text-caption text-chalk-dim">
                  {data.reactions.map((reaction) => (
                    <li key={reaction.id} className="flex items-center gap-2">
                      <span>{REACTION_EMOJI[reaction.kind] ?? reaction.kind}</span>
                      <Mono>{timecode(reaction.timecodeSec)}</Mono>
                      <span className="truncate">{reaction.authorUser ? reaction.authorUser.name?.trim() || reaction.authorUser.email : 'Someone'}</span>
                      <span className="ml-auto shrink-0 text-chalk-faint">{timeAgo(reaction.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Section>
      </div>

      <Section title="Comments" count={data.comments.length}>
        <div className="card divide-y divide-hairline">
          {data.comments.length === 0 ? (
            <p className="px-4 py-6 text-center text-body text-chalk-faint">None yet.</p>
          ) : (
            data.comments.map((comment) => (
              <div key={comment.id} className="px-4 py-3">
                <div className="flex items-center gap-2 text-caption text-chalk-faint">
                  {comment.authorUser ? (
                    <AppLink to="/users/$id" params={{ id: comment.authorUser.id }} className="text-chalk hover:underline">
                      {comment.authorUser.name?.trim() || comment.authorUser.email}
                    </AppLink>
                  ) : (
                    <span className="text-chalk">{comment.authorName}</span>
                  )}
                  {comment.timecodeSec !== null ? <Mono>{timecode(comment.timecodeSec)}</Mono> : null}
                  <span title={comment.createdAt}>{timeAgo(comment.createdAt)}</span>
                  {comment.resolvedAt ? <Badge tone="go">Resolved</Badge> : null}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-body text-chalk">{comment.bodyText}</p>
              </div>
            ))
          )}
        </div>
      </Section>

      <Section title="Transcript">
        <div className="card p-4">
          {!data.transcript ? (
            <p className="text-body text-chalk-faint">No transcript was ever started for this recording.</p>
          ) : data.transcript.status === 'ready' && data.transcript.text ? (
            <>
              <p className="mb-2 text-caption text-chalk-faint">
                {data.transcript.source === 'device' ? 'Transcribed on the device' : 'Transcribed by Amazon Transcribe'} · {data.transcript.language} ·{' '}
                {timeAgo(data.transcript.updatedAt)}
                {data.transcript.captionsUrl ? (
                  <>
                    {' '}
                    · <ExternalLink href={data.transcript.captionsUrl}>captions file</ExternalLink>
                  </>
                ) : null}
              </p>
              <p className="max-h-[32rem] overflow-auto whitespace-pre-wrap text-body leading-relaxed text-chalk-dim">{data.transcript.text}</p>
            </>
          ) : (
            <p className="text-body text-chalk-faint">
              {data.transcript.status === 'pending' ? 'Still being transcribed.' : data.transcript.status === 'failed' ? 'Transcription failed.' : 'Transcription was skipped for this recording.'}
            </p>
          )}
        </div>
      </Section>

      <Section title="Poster">
        <Thumbnail video={data} className="h-24 w-[10.5rem]" />
      </Section>
    </>
  );
}
